import {
  ApolloClient,
  ApolloLink,
  ApolloProvider,
  from,
  InMemoryCache,
  split,
} from '@apollo/client'
import { onError } from '@apollo/client/link/error'
import { GraphQLWsLink } from '@apollo/client/link/subscriptions'
import { getMainDefinition, print } from '@apollo/client/utilities'
import { emitEvent } from '@faceup/devtools'
import { useNotification } from '@faceup/ui-base'
import {
  buildUrl,
  DEFAULT_LANGUAGE,
  errorMessages,
  httpAnalyzerEnabled,
  httpAnalyzerName,
  INVALID_LOGIN_CREDENTIALS_ERROR,
  type Region,
  regionHeaderName,
} from '@faceup/utils'
// @ts-expect-error Ignore types https://github.com/jaydenseric/apollo-upload-client/releases/tag/v18.0.0#:~:text=Implemented%20TypeScript%20types%20via%20JSDoc%20comments.
import createUploadLink from 'apollo-upload-client/createUploadLink.mjs'
import { createClient } from 'graphql-ws'
import { type ReactNode, useContext, useMemo, useRef } from 'react'
import { possibleTypes } from './__generated__/possibleTypes.json'
import { LanguageContext } from './Contexts/LanguageContext'
import { httpAnalyzer } from './httpAnalyzer'
import { sharedMessages } from './Shared/translations'
import { useIntl } from './TypedIntl'
import useAuth from './utils/auth'
import useNetworkStatus from './utils/useNetworkStatus'
import { region } from './utils/useRegion'

/**
 * Add an error message here if you don't want to show the default error notification.
 */
const suppressedErrors: string[] = [
  INVALID_LOGIN_CREDENTIALS_ERROR,
  errorMessages.institutionDoesNotExist,
  errorMessages.reportSolvedNoNewChatMessages,
]

const CustomApolloProvider = ({ children }: { children?: ReactNode }) => {
  const networkStatus = useNetworkStatus()
  const { language } = useContext(LanguageContext)
  const { formatMessage } = useIntl()
  const notification = useNotification()
  const { getJwt } = useAuth()
  const { setWsRegion, wsLink } = useCreateWsLink()

  // Use ref to avoid dependency in errorLink
  // networkStatus changes from undefined to 'online'/'offline' after init
  const networkStatusRef = useRef(networkStatus)
  networkStatusRef.current = networkStatus

  // inspiration
  // > https://www.apollographql.com/docs/react/advanced/subscriptions.html
  // + file upload https://github.com/jaydenseric/apollo-upload-client#function-createuploadlink
  const httpLink = useMemo(
    () =>
      createUploadLink({
        uri: import.meta.env.VITE_API_URL ?? '',
        credentials: 'include',
        headers: { 'Apollo-Require-Preflight': 'true' },
      }),
    []
  )

  // biome-ignore lint/correctness/useExhaustiveDependencies: GetJwt and setWsRegion are stable functions
  const authLink = useMemo(
    () =>
      new ApolloLink((operation, forward) => {
        operation.setContext((context: Record<string, unknown>) => {
          const token = getJwt()
          setWsRegion(region)

          return {
            ...context,
            start: performance.now(),
            headers: {
              authorization: token ? `Bearer ${token}` : '',
              language,
              [regionHeaderName]: region,
              ...(httpAnalyzer.getIsEnabled() && { [httpAnalyzerEnabled]: 'true' }),
            },
          }
        })

        return forward(operation).map(response => {
          const xHttpAnalyzer = operation.getContext()['response'].headers.get(httpAnalyzerName)
          if (xHttpAnalyzer) {
            httpAnalyzer.handleXRequestFaceUpTracingJSON(xHttpAnalyzer)
          }

          emitEvent('cache', operation.getContext()['cache']?.data?.data)

          const parsedXHttpAnalyzer = (() => {
            try {
              return JSON.parse(xHttpAnalyzer ?? '{}')
            } catch {
              return {}
            }
          })()

          emitEvent('requests', {
            name: operation.operationName,
            query: print(operation.query),
            variables: operation.variables,
            response: response as Record<string, unknown>,
            httpAnalyzer: {
              ...parsedXHttpAnalyzer,
              duration: performance.now() - operation.getContext()['start'],
            },
            isError: 'errors' in response,
            source: 'gql',
          })

          return response
        })
      }),
    [language]
  )

  // biome-ignore lint/correctness/useExhaustiveDependencies: Notification.error and formatMessage are stable
  const errorLink = useMemo(
    () =>
      onError(({ graphQLErrors, networkError }) => {
        const suppressError = suppressedErrors.includes(graphQLErrors?.[0]?.message ?? '')

        if (suppressError || networkStatusRef.current !== 'online') {
          return
        }

        const errorMessage = networkError?.message ?? graphQLErrors?.[0]?.message ?? ''
        console.error(errorMessage)
        notification.error({
          message: formatMessage(sharedMessages.apiError),
          description: errorMessage,
        })
      }),
    []
  )

  const link = useMemo(
    () =>
      from([
        errorLink,
        split(
          // split based on operation type
          ({ query }) => {
            const definition = getMainDefinition(query)

            return (
              definition.kind === 'OperationDefinition' && definition.operation === 'subscription'
            )
          },
          wsLink,
          authLink.concat(httpLink)
        ),
      ]),
    [wsLink, errorLink, authLink, httpLink]
  )

  const apolloClient = useMemo(
    () =>
      new ApolloClient({
        link,
        cache: new InMemoryCache({ possibleTypes }),
      }),
    [link]
  )

  return <ApolloProvider client={apolloClient}>{children}</ApolloProvider>
}

const useCreateWsLink: () => {
  wsLink: GraphQLWsLink
  setWsRegion: (region: Region) => void
} = () => {
  const { language } = useContext(LanguageContext)
  const { getJwt } = useAuth()

  const createWsLink = (region: Region) => {
    return new GraphQLWsLink(
      createClient({
        url: buildUrl(import.meta.env.VITE_WS_API_URL ?? '', { [regionHeaderName]: region }),
        lazy: true,
        connectionParams: () => ({
          token: getJwt() || '',
          language: language || DEFAULT_LANGUAGE,
          origin: window.location.origin,
          [regionHeaderName]: region,
        }),
        retryAttempts: Infinity,
      })
    )
  }

  const linkObject = useRef<{ wsLink: GraphQLWsLink; region: Region }>({
    wsLink: createWsLink(region),
    region,
  })

  return {
    wsLink: linkObject.current.wsLink,
    setWsRegion: newRegion => {
      if (linkObject.current.region === newRegion) {
        return
      }
      const newWsLink = createWsLink(newRegion)
      linkObject.current = { wsLink: newWsLink, region: newRegion }
    },
  }
}

export default CustomApolloProvider
